<template>
  <BaseModal
    :heading="$t('register.salonized_pay.dashboard.business_details.bank_accounts.heading')"
    :loading="loading"
    small
    @close="$emit('close')"
    testId="sp-bank-accounts"
    grey
  >
    <BaseCard :noPadding="!failed">
      <BaseAlert
        v-if="failed"
        color="error"
        :text="$t('register.salonized_pay.dashboard.business_details.bank_accounts.load_error')"
        v-test="'sp-bank-accounts-error'"
      />
      <template v-else-if="session">
        <adyen-transfer-instrument-configuration
          v-if="configuring"
          :key="`configure-${configuring.transferInstrumentId}`"
          :rootlegalentityid="session.legalEntityId"
          :environment="session.environment"
          :locale="adyenLocale"
          :transferinstrumentid="configuring.transferInstrumentId"
          :fetchToken.prop="fetchToken"
          :settings.prop="configurationSettings"
          @complete="onChange"
          @close="configuring = null"
          v-test="'sp-bank-accounts-configuration'"
        ></adyen-transfer-instrument-configuration>
        <adyen-transfer-instrument-management
          v-else
          :key="`manage-${changes}`"
          :rootlegalentityid="session.legalEntityId"
          :environment="session.environment"
          :locale="adyenLocale"
          :fetchToken.prop="fetchToken"
          @add="configuring = { transferInstrumentId: undefined }"
          @edit="configuring = { transferInstrumentId: $event.value }"
          @remove="onChange"
          v-test="'sp-bank-accounts-management'"
        ></adyen-transfer-instrument-management>
      </template>
    </BaseCard>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useLocalizationStore } from '@/stores/localization';
import {
  CREATE_ADYEN_ONBOARDING_COMPONENT_SESSION,
  SYNC_ADYEN_LEGAL_ENTITY,
} from '../graphql';

type Session = {
  token: string;
  environment: string;
  legalEntityId: string;
};

defineEmits(['close']);

// The store's language, not i18n's locale: that one has a -tw suffix for
// Treatwell users. Adyen expects a full locale and falls back to en-US for
// one it does not support.
const { language } = storeToRefs(useLocalizationStore());
const adyenLocale = computed(() =>
  language.value === 'en'
    ? 'en-US'
    : `${language.value}-${language.value.toUpperCase()}`,
);

const session = ref<Session | null>(null);
const failed = ref(false);
const loading = ref(true);
const configuring = ref<{ transferInstrumentId?: string } | null>(null);
// Bumped after a change so the management component reloads the bank accounts.
const changes = ref(0);

// Hides the "Log in with your bank" (open banking) verification method, so a
// bank account is always added by entering its details.
const configurationSettings = { instantBankVerification: false };

const { mutate: createSession } = useMutation(
  CREATE_ADYEN_ONBOARDING_COMPONENT_SESSION,
);
// Refetch by name: queries default to no-cache, so the mutation result alone
// doesn't reach the bank accounts shown on the page.
const { mutate: syncLegalEntity } = useMutation(SYNC_ADYEN_LEGAL_ENTITY, {
  refetchQueries: [
    'getAdyenAppState',
    'getAdyenBusinessDetails',
    'getAdyenWallets',
  ],
});

const requestSession = async (): Promise<Session> => {
  const result = await createSession({ input: {} });
  const newSession =
    result?.data?.createAdyenOnboardingComponentSession?.session;

  if (!newSession) {
    throw new Error('No Adyen onboarding component session');
  }

  return newSession;
};

// The first session's token is used for the first fetch, after that the
// components ask for a fresh one whenever theirs expires.
let unusedToken: string | null = null;

const fetchToken = async () => {
  if (unusedToken) {
    const token = unusedToken;
    unusedToken = null;
    return { token };
  }

  const { token } = await requestSession();
  return { token };
};

// Adyen has no webhook for a new bank account until it is verified, so ask
// the backend to read it now; this also refreshes the list on the page.
const onChange = () => {
  syncLegalEntity({ input: {} });
  configuring.value = null;
  changes.value++;
};

Promise.all([
  requestSession(),
  import('@adyen/kyc-components/transfer-instrument-management'),
  import('@adyen/kyc-components/transfer-instrument-configuration'),
])
  .then(([newSession]) => {
    unusedToken = newSession.token;
    session.value = newSession;
  })
  .catch(() => {
    failed.value = true;
  })
  .finally(() => {
    loading.value = false;
  });
</script>
